import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";

const STORAGE_KEY = "valholl.tweaks.v2";
const ADMIN_KEY = "valholl.admin.authed";
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || "valhalla2025";
const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;

export const DEFAULT_TWEAKS = {
  appearance: {
    introOverlay: true,
    showLogoIntro: true,
    showLogoNav: true,
    grainOverlay: true,
    dustParticles: true,
    fogEnabled: true,
    auroraEnabled: true,
    showTopNav: true,
    showProgressBar: true,
    heroLayout: "text-left", // 'text-left' | 'text-right' | 'centered'
    heroEyebrow: "Tu vida merece ser celebrada · mivalhalla.com",
    heroTitle: "Tu propio Valhalla está hecho de recuerdos,\nno de piedra.",
    heroSubtitle:
      "My Valhalla es la plataforma para planificar tu legado emocional una manera de guardar un recuerdo digital con propósito y alegría. Mensajes al futuro, álbumes de vida, playlist de tus canciones favoritas — todo lo que quieres dejar, cuidado y entregado en el momento exacto.",
    finalQuote:
      "No construyes recuerdos para ti.\nLos construyes para quienes amas..",
    accentGold: "#c5a059",
    glassmorphismStrength: 24, // backdrop blur px
  },
  wpo: {
    lazyLoadImages: true,
    reduceMotion: false,
    imageQuality: "high", // 'low' | 'medium' | 'high'
    preloadHero: true,
    autoplayVideos: true,
    particleCount: 40, // 0-80
    smoothScroll: true,
    deferOffscreen: true,
  },
};

const TweaksContext = createContext({
  tweaks: DEFAULT_TWEAKS,
  setAppearance: () => { },
  setWpo: () => { },
  reset: () => { },
  isAdmin: false,
  loginAdmin: () => false,
  logoutAdmin: () => { },
  saveToBackend: async () => false,
});

function deepMerge(target, src) {
  const out = { ...target };
  for (const k of Object.keys(src || {})) {
    if (src[k] && typeof src[k] === "object" && !Array.isArray(src[k])) {
      out[k] = deepMerge(target[k] || {}, src[k]);
    } else {
      out[k] = src[k];
    }
  }
  return out;
}

export function TweaksProvider({ children }) {
  const [tweaks, setTweaks] = useState(DEFAULT_TWEAKS);
  const [isAdmin, setIsAdmin] = useState(false);

  // Hydrate: localStorage first (sync), then backend overrides (async)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setTweaks(deepMerge(DEFAULT_TWEAKS, JSON.parse(raw)));
      setIsAdmin(localStorage.getItem(ADMIN_KEY) === "1");
    } catch (e) {
      // localStorage unavailable — keep defaults
    }
    if (API) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      fetch(`${API}/tweaks`, { signal: controller.signal })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data && Object.keys(data).length > 0)
            setTweaks(deepMerge(DEFAULT_TWEAKS, data));
        })
        .catch(() => { })
        .finally(() => clearTimeout(timeoutId));
    }
  }, []);

  // Persist
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tweaks));
    } catch (e) {
      // ignore quota / private-mode errors
    }
  }, [tweaks]);

  // Apply smooth-scroll & reduce-motion globally
  useEffect(() => {
    document.documentElement.style.scrollBehavior = tweaks.wpo.smoothScroll ? "smooth" : "auto";
    document.documentElement.dataset.reduceMotion = tweaks.wpo.reduceMotion ? "true" : "false";
  }, [tweaks.wpo.smoothScroll, tweaks.wpo.reduceMotion]);

  const setAppearance = useCallback((patch) => {
    setTweaks((t) => ({ ...t, appearance: { ...t.appearance, ...patch } }));
  }, []);

  const setWpo = useCallback((patch) => {
    setTweaks((t) => ({ ...t, wpo: { ...t.wpo, ...patch } }));
  }, []);

  const reset = useCallback(() => setTweaks(DEFAULT_TWEAKS), []);

  const loginAdmin = useCallback((pwd) => {
    if (pwd === ADMIN_PASSWORD) {
      localStorage.setItem(ADMIN_KEY, "1");
      setIsAdmin(true);
      return true;
    }
    return false;
  }, []);

  const logoutAdmin = useCallback(() => {
    localStorage.removeItem(ADMIN_KEY);
    setIsAdmin(false);
  }, []);

  const saveToBackend = useCallback(async (tweaksData) => {
    if (!API) return false;
    try {
      const res = await fetch(`${API}/admin/tweaks`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Admin-Key": ADMIN_PASSWORD },
        body: JSON.stringify(tweaksData),
      });
      return res.ok;
    } catch {
      return false;
    }
  }, []);

  const value = useMemo(
    () => ({ tweaks, setAppearance, setWpo, reset, isAdmin, loginAdmin, logoutAdmin, saveToBackend }),
    [tweaks, setAppearance, setWpo, reset, isAdmin, loginAdmin, logoutAdmin, saveToBackend]
  );

  return <TweaksContext.Provider value={value}>{children}</TweaksContext.Provider>;
}

export const useTweaks = () => useContext(TweaksContext);
